import { useState, useEffect } from 'react'
import { X, Edit2, History, RotateCcw } from 'lucide-react'
import { Card, Badge, Button, Input, Textarea, Select, Divider, SectionHeading } from '../../../shared/components/UI'
import SplitEditorLayout from '../../../shared/components/SplitEditorLayout'
import { announcementsApi, unitsApi } from '../../../shared/lib/api'

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'metallic_master', label: 'Metallic Master only' },
  { value: 'loading_supervisor', label: 'Loading Supervisor only' },
]

const ROLE_LABEL_MAP = { '': 'All Roles', metallic_master: 'Metallic Master', loading_supervisor: 'Loading Supervisor' }

const empty = { title: '', message: '', targetUnitId: '', targetRole: '' }

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(empty)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    Promise.all([
      announcementsApi.list(),
      unitsApi.list()
    ]).then(([annRes, unitRes]) => {
      setAnnouncements(annRes.data.data)
      setUnits(unitRes.data.data)
    }).finally(() => setLoading(false))
  }, [])

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const startNew = () => { setEditing('new'); setForm(empty) }
  const cancel = () => { setEditing(null); setForm(empty) }

  const save = async () => {
    if (!form.title.trim() || !form.message.trim()) return
    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      targetUnitId: form.targetUnitId || null,
      targetRole: form.targetRole || null,
    }

    setIsSaving(true)
    try {
      if (editing === 'new') {
        await announcementsApi.create(payload)
      } else {
        await announcementsApi.update(editing, payload)
      }
      const res = await announcementsApi.list()
      setAnnouncements(res.data.data)
      cancel()
    } catch (err) {
      console.error('Failed to save announcement:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleExpire = async (id) => {
    try {
      await announcementsApi.expire(id)
      setAnnouncements((p) => p.map((a) => a.id === id ? { ...a, status: 'expired' } : a))
    } catch (err) {
      console.error('Failed to expire announcement:', err)
    }
  }

  const handleReactivate = async (id) => {
    try {
      await announcementsApi.reactivate(id)
      setAnnouncements((p) => p.map((a) => a.id === id ? { ...a, status: 'active' } : a))
    } catch (err) {
      console.error('Failed to reactivate announcement:', err)
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Loading announcements...</div>

  const active = announcements.filter((a) => a.status === 'active')
  const expired = announcements.filter((a) => a.status === 'expired')

  return (
    <SplitEditorLayout
      isOpen={!!editing}
      left={
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="green">{active.length} active</Badge>
              <Badge variant="gray">{expired.length} expired</Badge>
            </div>
            <Button onClick={startNew}>+ New Announcement</Button>
          </div>

          {active.length > 0 && (
            <div className="space-y-3">
              <SectionHeading>Active</SectionHeading>
              {active.map((a) => (
                <AnnouncementCard
                  key={a.id}
                  a={a}
                  onEdit={() => {
                    setEditing(a.id)
                    setForm({
                      title: a.title,
                      message: a.message,
                      targetUnitId: a.target_unit_id || '',
                      targetRole: a.target_role || ''
                    })
                  }}
                  onExpire={() => handleExpire(a.id)}
                />
              ))}
            </div>
          )}

          {expired.length > 0 && (
            <div className="space-y-3">
              <SectionHeading>Expired</SectionHeading>
              {expired.map((a) => (
                <AnnouncementCard
                  key={a.id}
                  a={a}
                  expired
                  onReactivate={() => handleReactivate(a.id)}
                />
              ))}
            </div>
          )}

          {active.length === 0 && expired.length === 0 && (
            <div className="bg-white border border-dashed border-gray-200 rounded-xl p-12 text-center text-gray-400">
              No announcements posted yet
            </div>
          )}
        </div>
      }
      right={editing && (
        <Card
          title={editing === 'new' ? 'New Announcement' : 'Edit Announcement'}
          action={
            <button
              onClick={cancel}
              className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close panel"
            >
              <X size={16} />
            </button>
          }
          className="sticky top-2 shadow-xl border border-brand-100"
        >
          <Input label="Title" value={form.title} onChange={f('title')} placeholder="e.g. Machine maintenance today" />
          <Textarea
            label="Message"
            value={form.message}
            onChange={f('message')}
            placeholder="Write the announcement text..."
            className="min-h-[100px]"
          />

          <Divider />
          <SectionHeading>Who should see this?</SectionHeading>

          <Select
            label="Unit"
            value={form.targetUnitId}
            onChange={f('targetUnitId')}
            options={[
              { label: 'All Units', value: '' },
              ...units.map((u) => ({ label: u.name, value: u.id }))
            ]}
          />
          <Select label="Role" value={form.targetRole} onChange={f('targetRole')} options={ROLE_OPTIONS} />

          <div className="bg-brand-50 rounded-lg p-3 text-xs text-brand-700 mb-4">
            This will show to: <strong>{ROLE_LABEL_MAP[form.targetRole]}</strong> in <strong>{form.targetUnitId ? (units.find((u) => u.id === form.targetUnitId)?.name || 'Unit') : 'All Units'}</strong>
          </div>

          <Divider />
           <div className="flex gap-2">
            <Button onClick={save} disabled={isSaving || !form.title.trim() || !form.message.trim()}>
              {isSaving ? 'Publishing...' : (editing === 'new' ? 'Publish' : 'Save Changes')}
            </Button>
            <Button variant="secondary" onClick={cancel} disabled={isSaving}>Cancel</Button>
          </div>
        </Card>
      )}
    />
  )
}

function AnnouncementCard({ a, onEdit, onExpire, onReactivate, expired }) {
  const dt = new Date(a.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

  return (
    <div className={`bg-white border rounded-xl p-4 ${expired ? 'opacity-50' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">{a.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{dt} • by {a.creator?.name || 'Admin'}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Badge variant={!a.target_unit_id ? 'gray' : 'blue'}>{a.targetUnit?.name || 'All Units'}</Badge>
          <Badge variant={!a.target_role ? 'gray' : 'teal'}>{ROLE_LABEL_MAP[a.target_role || '']}</Badge>
        </div>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed mb-3">{a.message}</p>
      <div className="flex gap-2">
        {!expired && (
          <>
            <button 
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
              title="Edit"
            >
              <Edit2 size={16} />
            </button>
            <button 
              onClick={onExpire}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
              title="Expire"
            >
              <History size={16} />
            </button>
          </>
        )}
        {expired && (
          <button 
            onClick={onReactivate}
            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
            title="Re-activate"
          >
            <RotateCcw size={16} />
          </button>
        )}
      </div>
    </div>
  )
}
